import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { processScheduleService } from '~/services/workspaceAPI';
import { ProcessSchedule } from '~/types/resources';
import { useLazyQuery } from '../useLazyQuery';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';

const ROOT_KEY = 'schedules';
const getSchedulesQueryKey = () => [ROOT_KEY];
export const getProcessSchedulesQueryKey = (processId: string) => [ROOT_KEY, processId];

export const useProcessesSchedulesQuery = (
  options?: UseApiQueryOptions<typeof processScheduleService.listWorkspaceSchedules>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getSchedulesQueryKey()),
    async () =>
      validateApiResponse(await processScheduleService.listWorkspaceSchedules({ workspaceId }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessSchedulesQuery = (
  processId: string,
  options?: UseApiQueryOptions<typeof processScheduleService.getSchedulesByProcessId>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getProcessSchedulesQueryKey(processId),
    async () =>
      validateApiResponse(
        await processScheduleService.getSchedulesByProcessId({ workspaceId, processId }),
      ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessSchedulesLazyQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<{ processId: string }, ProcessSchedule[] | undefined, Error>(
    ({ processId }) => getProcessSchedulesQueryKey(processId),
    ({ processId }) =>
      async () =>
        validateApiResponse(
          await processScheduleService.getSchedulesByProcessId({ workspaceId, processId }),
        ).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
    },
  );
};

export const useCreateScheduleMutation = <T extends typeof processScheduleService.createSchedule>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      schedule,
    }: {
      processId: string;
      schedule: Omit<ApiQueryFnData<T>, 'id'>;
    }) => {
      const response = validateApiResponse(
        await processScheduleService.createSchedule({
          workspaceId,
          processId,
          schedule: { ...schedule, id: undefined },
        }),
      );
      return response.data;
    },
    {
      onSuccess: (_, { processId }) =>
        queryClient.invalidateQueries(getProcessSchedulesQueryKey(processId)),
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
export const useEditScheduleMutation = <T extends typeof processScheduleService.editRobotSchedule>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      scheduleId,
      schedule,
    }: {
      processId: string;
      scheduleId: string;
      schedule: ApiQueryFnData<T>;
    }) =>
      validateApiResponse(
        await processScheduleService.editRobotSchedule({
          workspaceId,
          processId,
          scheduleId,
          schedule,
        }),
      ).data,
    {
      onSuccess: (_, { processId }) =>
        queryClient.invalidateQueries(getProcessSchedulesQueryKey(processId)),
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteScheduleMutation = (
  options?: UseApiMutationOptions<typeof processScheduleService.deleteRobotSchedule>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      validateApiResponse(
        await processScheduleService.deleteRobotSchedule({ workspaceId, processId, scheduleId }),
      ).data,
    {
      onSuccess: (_, { processId }) =>
        queryClient.invalidateQueries(getProcessSchedulesQueryKey(processId)),
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
